/**
 * Instância global do QueryClient utilizada para gerenciar o cache e o estado das queries
 * em toda a aplicação React.
 *
 * Observabilidade: erros que NÃO são customError (AppError mapeado) são capturados
 * no Sentry de forma centralizada. Esses são exatamente os que caem na mensagem
 * genérica na UI — falha de rede/sem resposta, status HTTP não mapeado (ex.: 423/503)
 * ou erro de parsing —, sem texto do backend para exibir. Erros de negócio
 * (customError) já trazem mensagem própria e não são ruído de observabilidade.
 *
 * @see {@link https://react-query.tanstack.com/reference/QueryClient}
 */

import { AppError } from '@/domain/errors'
import { probeReachabilityOnce } from '@/infra/http/connectivity-probe'
import { NetworkError } from '@/infra/http/network-error'
import { emitNetworkError } from '@/infra/http/network-error-notifier'
import errorMonitor from '@/infra/tracking/error-monitor'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import axios from 'axios'

const serializeKey = (key?: unknown): string | undefined => {
  if (!key) return undefined
  try {
    return JSON.stringify(key)
  } catch {
    return '[Unserializable Key]'
  }
}

export const reportGenericError = async (
  error: unknown,
  source: 'query' | 'mutation',
  key?: unknown
) => {
  // Cancelamento (navegação/AbortSignal) não é falha: não reportar (evita ruído
  // de CanceledError no Sentry, já que o AxiosHttpClient propaga o cancel).
  if (axios.isCancel(error)) return

  // Falha sem resposta (rede/CORS/proxy). É customError, então cairia no
  // early-return abaixo; reportamos explicitamente com tag dedicada e nível
  // warning (não é nossa falha — não polui a taxa de erro), preservando o
  // correlationId para correlacionar com os logs do LB/suporte. O veredito do
  // probe (offline/reachable/unreachable) é o sinal mais acionável — `reachable`
  // indica bloqueio seletivo (proxy/DLP corporativo).
  if (error instanceof NetworkError) {
    // Dispara o snackbar global (mensagem amigável imediata + refino). Único
    // ponto de origem para a UI de erro de rede em todo o app.
    emitNetworkError(error)

    // Telemetria não pode derrubar a aplicação: como é chamada com `void` nos
    // caches, uma rejeição aqui viraria unhandled promise rejection.
    try {
      const verdict = await probeReachabilityOnce(error)
      errorMonitor.captureException(error, {
        tags: { network_error: 'true', network_verdict: verdict, source },
        extra: {
          queryKey: serializeKey(key),
          correlationId: error.correlationId,
          requestUrl: error.requestUrl,
          errorName: error.name,
          errorMessage: error.message
        },
        level: 'warning'
      })
    } catch (reportingError) {
      console.error('Failed to report network error:', reportingError)
    }
    return
  }

  if ((error as AppError)?.customError) return

  errorMonitor.captureException(error, {
    tags: {
      generic_error: 'true',
      source,
      http_status: String((error as AppError)?.statusCode ?? 'no_response')
    },
    extra: {
      queryKey: serializeKey(key),
      correlationId: (error as { correlationId?: string })?.correlationId,
      errorName: (error as Error)?.name,
      errorMessage: (error as Error)?.message
    },
    level: 'error'
  })
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => void reportGenericError(error, 'query', query.queryKey)
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      void reportGenericError(error, 'mutation', mutation.options.mutationKey)
  })
})

export { queryClient }
